import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Spinner,
  Alert,
  Chip,
  Tabs,
  EmptyState,
} from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { NEUTRAL_BTN } from '../lib/buttonStyles';
import {
  adminApiClient,
  AdminApiError,
  type RecipeListItem,
  type RecipeFacet,
  type RecipeStatusFilter,
} from '../lib/adminApiClient';

/**
 * Recipe moderation (recipe:moderate).
 * - Publish / unpublish recipes (toggles is_published).
 * - Reclassify mis-tagged cuisine / meal types (free String[] columns; the
 *   dropdown helper is powered by the live distinct-values facets endpoint, so
 *   it never drifts from a hardcoded list).
 * Server is the source of truth — every action re-checks the capability and is
 * audited (AdminAuditLog). Recipes carry no UPC, so nothing to omit here.
 */

const parseTags = (s: string): string[] =>
  s
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

/** A friendly recipe handle: GLPR id if minted, else a short UUID prefix. */
const recipeHandle = (r: RecipeListItem): string =>
  r.glpRecipeId ?? `${r.recipeId.slice(0, 8)}…`;

export default function RecipeModeration() {
  const caps = useCapabilities();
  const canView = caps.has('recipe:read');
  const canModerate = caps.has('recipe:moderate');

  const [status, setStatus] = useState<RecipeStatusFilter>('all');
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<RecipeListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [facets, setFacets] = useState<{ cuisineTypes: RecipeFacet[]; mealTypes: RecipeFacet[] }>({
    cuisineTypes: [],
    mealTypes: [],
  });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.listRecipes({
        status,
        q: search || undefined,
        limit: 50,
      });
      setItems(res.items);
      setNextCursor(res.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load recipes');
    } finally {
      setLoading(false);
    }
  }, [status, search]);

  const loadMore = useCallback(async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const res = await adminApiClient.listRecipes({
        status,
        q: search || undefined,
        before: nextCursor,
        limit: 50,
      });
      setItems((prev) => [...prev, ...res.items]);
      setNextCursor(res.nextCursor);
    } catch (err) {
      setFlash({ kind: 'error', msg: err instanceof Error ? err.message : 'Failed to load more' });
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, status, search]);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  // Facets once — power the reclassify helper. Non-fatal if it fails.
  useEffect(() => {
    if (!canView) return;
    void adminApiClient
      .recipeFacets()
      .then(setFacets)
      .catch(() => {});
  }, [canView]);

  const onCardDone = (kind: 'success' | 'error', msg: string, updated?: RecipeListItem) => {
    setFlash({ kind, msg });
    if (updated) {
      setItems((prev) => prev.map((r) => (r.recipeId === updated.recipeId ? updated : r)));
    }
  };

  if (caps.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }
  if (!canView) {
    return <Alert severity="error">You don’t have permission to view recipes.</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        Recipe moderation
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Publish or unpublish recipes and fix mis-classified cuisine / meal tags. Every change is
        audited.
      </Typography>

      {!canModerate && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">View-only access — you can browse but can’t make changes.</Alert>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Tabs
          ariaLabel="Recipe publish status"
          value={status}
          onChange={(_e, v) => setStatus(v as RecipeStatusFilter)}
          tabs={[
            { value: 'all', label: 'All' },
            { value: 'published', label: 'Published' },
            { value: 'unpublished', label: 'Unpublished' },
          ]}
        />
        <Box sx={{ flex: 1 }} />
        <TextField
          label="Search title"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          size="small"
          sx={{ width: 240 }}
        />
        <Button
          variant="outlined"
          sx={NEUTRAL_BTN}
          ariaLabel="Apply title search"
          buttonText="Search"
          onClick={() => setSearch(searchDraft.trim())}
        />
      </Box>

      {flash && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={flash.kind === 'success' ? 'success' : 'error'}>{flash.msg}</Alert>
        </Box>
      )}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Spinner color="brand" />
        </Box>
      ) : items.length === 0 ? (
        <EmptyState icon="restaurant" title="No recipes" body="No recipes match this filter." />
      ) : (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {items.map((r) => (
              <RecipeCard key={r.recipeId} recipe={r} facets={facets} canWrite={canModerate} onDone={onCardDone} />
            ))}
          </Box>
          {nextCursor && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                sx={NEUTRAL_BTN}
                ariaLabel="Load more recipes"
                buttonText={loadingMore ? 'Loading…' : 'Load more'}
                disabled={loadingMore}
                onClick={() => void loadMore()}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

function RecipeCard({
  recipe,
  facets,
  canWrite,
  onDone,
}: {
  recipe: RecipeListItem;
  facets: { cuisineTypes: RecipeFacet[]; mealTypes: RecipeFacet[] };
  canWrite: boolean;
  onDone: (kind: 'success' | 'error', msg: string, updated?: RecipeListItem) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [cuisineDraft, setCuisineDraft] = useState(recipe.cuisineTypes.join(', '));
  const [mealDraft, setMealDraft] = useState(recipe.mealTypes.join(', '));

  const topCuisines = facets.cuisineTypes.slice(0, 10).map((f) => f.value);
  const topMeals = facets.mealTypes.slice(0, 10).map((f) => f.value);

  const togglePublish = async () => {
    setBusy(true);
    try {
      if (recipe.isPublished) {
        await adminApiClient.unpublishRecipe(recipe.recipeId);
        onDone('success', `Unpublished “${recipe.title}”.`, { ...recipe, isPublished: false });
      } else {
        await adminApiClient.publishRecipe(recipe.recipeId);
        onDone('success', `Published “${recipe.title}”.`, { ...recipe, isPublished: true });
      }
    } catch (err) {
      onDone('error', err instanceof AdminApiError ? err.message : 'Failed to update publish state');
    } finally {
      setBusy(false);
    }
  };

  const saveTags = async () => {
    const cuisineTypes = parseTags(cuisineDraft);
    const mealTypes = parseTags(mealDraft);
    setBusy(true);
    try {
      await adminApiClient.classifyRecipe(recipe.recipeId, { cuisineTypes, mealTypes });
      onDone('success', `Reclassified “${recipe.title}”.`, { ...recipe, cuisineTypes, mealTypes });
      setEditing(false);
    } catch (err) {
      onDone('error', err instanceof AdminApiError ? err.message : 'Failed to reclassify');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box
          aria-hidden
          sx={{
            width: 64,
            height: 64,
            borderRadius: 1,
            flexShrink: 0,
            bgcolor: '#e0e0e0',
            backgroundImage: recipe.imageThumbUrl ? `url(${recipe.imageThumbUrl})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {recipe.title}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {recipeHandle(recipe)} · {recipe.source}
            {recipe.ratingCount > 0
              ? ` · ★ ${recipe.ratingAvg.toFixed(1)} (${recipe.ratingCount})`
              : ''}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              label={recipe.isPublished ? 'Published' : 'Unpublished'}
              color={recipe.isPublished ? 'success' : 'default'}
              size="small"
            />
            {recipe.isFeatured && <Chip label="Featured" color="primary" size="small" />}
            {recipe.cuisineTypes.map((c) => (
              <Chip key={`c-${c}`} label={c} size="small" />
            ))}
            {recipe.mealTypes.map((m) => (
              <Chip key={`m-${m}`} label={m} color="primary" size="small" />
            ))}
          </Box>
        </Box>
        {canWrite && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
            <Button
              variant="contained"
              color={recipe.isPublished ? 'error' : 'primary'}
              ariaLabel={recipe.isPublished ? 'Unpublish recipe' : 'Publish recipe'}
              buttonText={recipe.isPublished ? 'Unpublish' : 'Publish'}
              disabled={busy}
              onClick={() => void togglePublish()}
            />
            <Button
              variant="outlined"
              sx={NEUTRAL_BTN}
              ariaLabel="Edit cuisine and meal tags"
              buttonText={editing ? 'Cancel' : 'Reclassify'}
              disabled={busy}
              onClick={() => setEditing((v) => !v)}
            />
          </Box>
        )}
      </Box>

      {canWrite && editing && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            label="Cuisine types (comma-separated)"
            value={cuisineDraft}
            onChange={(e) => setCuisineDraft(e.target.value)}
            size="small"
            helperText={topCuisines.length ? `Common: ${topCuisines.join(', ')}` : undefined}
            fullWidth
          />
          <TextField
            label="Meal types (comma-separated)"
            value={mealDraft}
            onChange={(e) => setMealDraft(e.target.value)}
            size="small"
            helperText={topMeals.length ? `Common: ${topMeals.join(', ')}` : undefined}
            fullWidth
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              ariaLabel="Save reclassification"
              buttonText="Save tags"
              disabled={busy}
              onClick={() => void saveTags()}
            />
            <Button
              variant="outlined"
              sx={NEUTRAL_BTN}
              ariaLabel="Cancel reclassification"
              buttonText="Cancel"
              disabled={busy}
              onClick={() => {
                setCuisineDraft(recipe.cuisineTypes.join(', '));
                setMealDraft(recipe.mealTypes.join(', '));
                setEditing(false);
              }}
            />
          </Box>
        </Box>
      )}
    </Paper>
  );
}
